import axios from "axios";
import store from "../redux/store";
import {
  API_ADD_PAYMENT_CARD_CUSTOMER,
  API_ADD_PROMOTION,
  API_CHANGE_ORDER_TYPE,
  API_CHECK_USER_EXISTENCE,
  API_CHECK_USER_EXISTENCE_BY_EMAIL,
  API_DELETE_CARD_CUSTOMER,
  API_DELETE_CUSTOMER_ACCOUNT,
  API_GET_GEOCODE_FROM_ADDRESS,
  API_GET_GEOCODE_FROM_PLACE_ID,
  API_GET_PLACES_AUTOCOMPLETE,
  API_UPDATE_PAYMENT_CARD_CUSTOMER,
  API_USER,
  WEBAPP,
} from "../constants";
import {
  getFirebaseCustomerId,
  getIdToken,
} from "./firebase/firebaseAuthentication";
import { decrypt, handleJoiSchemaError } from "../utils";
import {
  logHighError,
  logLowError,
  logMediumError,
  logMediumLog,
} from "../utils/logger";

const isDev = false;

export const DEFAULT_URL_DOMAIN = isDev
  ? "http://localhost:3000"
  : "https://api.dishop.co";

export const initializeBackend = (domain) => {
  // domain = 'https://dev-backend.dishop.co.ngrok.dev';
  // domain = 'http://localhost:3000';
  axios.defaults.baseURL = String(domain);
  axios.interceptors.request.use(
    async (config) => {
      try {
        const idToken = await getIdToken();

        config.headers = {
          ...(config.headers || {}),
          ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        };
      } catch (error) {
        logLowError("Could not fetch ID token:", error);
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const { config, response } = error || {};
      if (!response || !config) {
        return Promise.reject(error);
      }
      if (response.status === 403 && !config._retry403) {
        config._retry403 = true;
        try {
          const freshToken = await getIdToken(true);
          if (freshToken) {
            config.headers = {
              ...(config.headers || {}),
              Authorization: `Bearer ${freshToken}`,
            };
          }
          return axios(config);
        } catch (e) {
          return Promise.reject(error);
        }
      }
      return Promise.reject(error);
    },
  );

  return true;
};

const getAuthHeaders = async () => {
  const idToken = await getIdToken();
  return { Authorization: `Bearer ${idToken}` };
};

export const sendPostRequest = async (
  url,
  data,
  addCompanyIdToBody = true,
  authHeaders,
) => {
  const { companyId } = store.getState().configurationReducer;
  const newData = addCompanyIdToBody ? { ...data, companyId } : data;
  const headers = authHeaders ?? (await getAuthHeaders());
  try {
    return await axios({
      method: "post",
      url,
      headers,
      data: newData,
    });
  } catch (error) {
    logHighError(
      `Error API CALL - ${url} - ${JSON.stringify(
        data ?? {},
      )} - ${JSON.stringify(error)}.`,
      { url, newData },
    );
    throw error.response ? error.response.data : error;
  }
};

export const sendGetRequest = async (url, data, addCompanyIdParams = true) => {
  let newData = data;
  if (addCompanyIdParams) {
    const { companyId } = store.getState().configurationReducer;
    newData = { ...data, companyId };
  }
  try {
    return await axios({
      method: "get",
      url,
      params: newData,
    });
  } catch (error) {
    logHighError(
      `GET Error API CALL - ${url} - ${JSON.stringify(
        data ?? {},
      )} - ${JSON.stringify(error ?? {})}.`,
      { url, newData },
    );
    throw error.response ? error.response.data.errorMessage : error;
  }
};

export const sendPutRequest = async (url, data) => {
  try {
    return await axios.put(url, data);
  } catch (error) {
    logHighError(
      `PUT Error API CALL - ${url} - ${JSON.stringify(
        data ?? {},
      )} - ${JSON.stringify(error ?? {})}.`,
      { url, data },
    );
    throw error.response ? error.response.data.errorMessage : error;
  }
};

export const sendDeleteRequest = async (url) => {
  try {
    return await axios.delete(url);
  } catch (error) {
    logHighError(
      `DELETE Error API CALL - ${url} - ${JSON.stringify(error ?? {})}.`,
      { url },
    );
    throw error.response ? error.response.data.errorMessage : error;
  }
};

export const addPromotionBackend = async (customerId, promoCode, deviceId) => {
  try {
    const { shopId } = store.getState().pendingOrderReducer;
    const data = {
      customerId,
      promoCode,
      deviceId,
      shopId,
      canal: WEBAPP,
    };
    const response = await sendPostRequest(API_ADD_PROMOTION, data);

    return response.data;
  } catch (error) {
    throw error?.errorMessage ?? error;
  }
};

export const createCustomerBackend = async (user) => {
  await getIdToken(true);
  const { shopId } = store.getState().shopReducer;
  const data = {
    user,
    shopId,
  };
  const { companyId } = store.getState().configurationReducer;
  const url = API_USER(companyId, user.id);
  await sendPostRequest(url, data, false);
  await getIdToken(true);
};

export const getCustomerBackend = async (customerId) => {
  try {
    if (customerId) {
      const { companyId } = store.getState().configurationReducer;
      const url = API_USER(companyId, customerId);
      await getIdToken(true);
      const response = await sendGetRequest(url, {}, false);
      return response.data;
    }
  } catch (error) {
    logMediumError(
      `Could not get customer backend ${handleJoiSchemaError(error)}`,
      { customerId },
    );
  }
};

export const checkCustomerExistenceBackend = async (customerId) => {
  const { companyId } = store.getState().configurationReducer;
  const url = API_CHECK_USER_EXISTENCE(companyId, customerId);
  try {
    const response = await sendGetRequest(url, {}, false);
    return response.data;
  } catch (err) {
    return false;
  }
};

export const checkCustomerExistenceByEmailBackend = async (email) => {
  const { companyId } = store.getState().configurationReducer;
  const url = API_CHECK_USER_EXISTENCE_BY_EMAIL(companyId);
  try {
    const response = await sendGetRequest(url, { email }, false);
    return response.data;
  } catch (err) {
    return false;
  }
};

export const updateCustomerPaymentCardBackend = async (newCardId) => {
  const { user } = store.getState().userReducer;
  const data = {
    newCardId,
    customerId: user.customerId,
  };
  await sendPostRequest(API_UPDATE_PAYMENT_CARD_CUSTOMER, data);
};

export const addCustomerPaymentCardBackend = async (newCard) => {
  const { user } = store.getState().userReducer;
  const data = {
    newCard,
    customerId: user.customerId,
  };
  await sendPostRequest(API_ADD_PAYMENT_CARD_CUSTOMER, data);
};

export const deleteCustomerPaymentCardBackend = async (cardId) => {
  const { user } = store.getState().userReducer;
  const data = {
    cardId,
    customerId: user.customerId,
  };
  await sendPostRequest(API_DELETE_CARD_CUSTOMER, data);
};

export const deleteCustomerAccountBackend = async () => {
  const customerId = getFirebaseCustomerId();
  if (customerId) {
    logMediumLog(`Delete user ${customerId}`, { customerId });
    const { shopId } = store.getState().shopReducer;
    const data = {
      customerId,
      shopId,
    };
    await sendPostRequest(API_DELETE_CUSTOMER_ACCOUNT, data);
  }
};

export const updateUser = async (userId, updatedUserData) => {
  const { companyId } = store.getState().configurationReducer;
  try {
    const url = API_USER(companyId, userId);
    const response = await sendPutRequest(url, updatedUserData);
    return response.data;
  } catch (error) {
    logMediumError(
      `Could not update user ${userId} of company ${companyId} ${handleJoiSchemaError(
        error,
      )}`,
      { updatedUserData, userId },
    );
    throw error;
  }
};

export const changeOrderTypeEndpoint = async (order, newOrderType) => {
  const { companyId } = store.getState().configurationReducer;
  try {
    const data = { nature: newOrderType };
    const url = API_CHANGE_ORDER_TYPE(companyId, order.id, order.chargeId);
    const response = await sendPutRequest(url, data);
    return response.data;
  } catch (error) {
    logMediumError(
      `Could not changer order type from ${
        order.orderType
      } to ${newOrderType} of company ${companyId} ${handleJoiSchemaError(
        error,
      )}`,
      { order, newOrderType },
    );
    throw error;
  }
};

export const getPlacesAutocomplete = async (placeInput) => {
  try {
    const data = { placeInput };
    const { companyId } = store.getState().configurationReducer;
    const result = await sendGetRequest(
      API_GET_PLACES_AUTOCOMPLETE(companyId),
      data,
      false,
    );
    return result.data;
  } catch (error) {
    logMediumError(
      `Could not get autocomplete for address ${placeInput} ${handleJoiSchemaError(
        error,
      )}`,
      { placeInput },
    );
    throw error;
  }
};

export const getGeocodeByAddress = async (address) => {
  try {
    const data = { address };
    const { companyId } = store.getState().configurationReducer;
    const response = await sendGetRequest(
      API_GET_GEOCODE_FROM_ADDRESS(companyId),
      data,
      false,
    );
    return response.data.results;
  } catch (error) {
    logMediumError(
      `Could not get geocode for address : ${address} ${handleJoiSchemaError(
        error,
      )}`,
      { address },
    );
    throw error;
  }
};

export const getGeocodeByPlaceId = async (placeId) => {
  const { companyId } = store.getState().configurationReducer;
  try {
    const data = { placeId };
    const response = await sendGetRequest(
      API_GET_GEOCODE_FROM_PLACE_ID(companyId),
      data,
      false,
    );
    return response.data.results;
  } catch (error) {
    logMediumError(
      `Could not fetch firebase credentials of company ${companyId} : ${handleJoiSchemaError(
        error,
      )}`,
      { placeId },
    );
    throw error;
  }
};

export const getCompanyFirebaseCredentials = async (companyId) => {
  const determineBaseUrl = () => {
    const windowUrl = window.location.origin;
    if (windowUrl?.includes("dev-webapp") || !windowUrl) {
      return `https://dev.api.dishop.co`;
    }
    if (windowUrl?.includes("localhost")) {
      return "https://dev.api.dishop.co";
    }
    return `https://api.dishop.co`;
  };

  try {
    const baseUrl = determineBaseUrl();
    const encryptedFirebaseCredentials = await axios({
      method: "get",
      url: `${baseUrl}/api/firebase/credentials`,
      params: {
        companyId,
      },
    });
    const decryptedFirebaseCredentials = await decrypt(
      encryptedFirebaseCredentials?.data,
    );
    return decryptedFirebaseCredentials
      ? JSON.parse(decryptedFirebaseCredentials)
      : null;
  } catch (error) {
    logMediumError(
      `Could not fetch firebase credentials of company ${companyId} : ${error}`,
    );
    throw error;
  }
};
